import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../../../components/dashboard/Sidebar";
import { BsSuitHeartFill, BsSuitHeart } from "react-icons/bs";
import { MdLocationOn, MdEdit, MdDelete } from "react-icons/md";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css";

import Cairo from "../../../assets/images/CairoHotel.jfif";
import lux from "../../../assets/images/lux.jpeg";
import elgouna from "../../../assets/images/elgouna.jpeg";
import dahab from "../../../assets/images/dahab.jpeg";
import nuwiba from "../../../assets/images/nwee.webp";

gsap.registerPlugin(ScrollTrigger);

const Trips = () => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([
    {
      _id: "trip1011",
      title: "Explore Cairo",
      price: "400.00",
      duration: "7 Days",
      location: "Cairo, Egypt",
      badge: "Top Destination",
      description: "Visit the Great Pyramids, the Sphinx, and the famous Egyptian Museum.",
      img: Cairo,
    },
    {
      _id: "trip1012",
      title: "Luxor Adventure",
      price: "850.00",
      duration: "10 Days",
      location: "Luxor, Egypt",
      badge: "Exclusive",
      description: "A journey to ancient temples, tombs, and the Valley of the Kings.",
      img: lux,
    },
    {
      _id: "trip1013",
      title: "Sailing the Nile",
      price: "950.00",
      duration: "7 Days",
      location: "Nile River, Egypt",
      badge: "Luxury",
      description: "Take a luxurious cruise down the Nile to experience Egypt's rich heritage.",
      img: elgouna,
    },
    {
      _id: "trip1014",
      title: "Dahab Beach Holiday",
      price: "600.00",
      duration: "5 Days",
      location: "Dahab, Egypt",
      description: "Enjoy the beautiful beaches, diving, and relaxation in Dahab.",
      img: dahab,
    },
    {
      _id: "trip1015",
      title: "Nuweiba Adventure",
      price: "350.00",
      duration: "5 Days",
      location: "Nuweiba, Egypt",
      description: "Explore the tranquil beaches and waters of Nuweiba.",
      img: nuwiba,
    },
  ]);

  const [wishList, setWishList] = useState([]);
  const [newTrip, setNewTrip] = useState({
    title: "",
    price: "",
    img: "",
    description: "",
    location: "",
    duration: "",
  });
  const [editTrip, setEditTrip] = useState(null);

  // Handle wish list
  const handleWishList = (trip) => {
    if (wishList.some((item) => item._id === trip._id)) {
      setWishList(wishList.filter((item) => item._id !== trip._id));
      toast.info("Removed from Wish List");
    } else {
      setWishList([...wishList, trip]);
      toast.success("Added to Wish List");
    }
  };

  // Handle delete trip
  const handleDeleteTrip = (id) => {
    setTrips(trips.filter((trip) => trip._id !== id));
    toast.error("Trip Deleted");
  };

  // Handle add/edit trip
  const handleAddTrip = () => {
    if (!newTrip.title || !newTrip.price || !newTrip.img || !newTrip.location || !newTrip.duration || !newTrip.description) {
      toast.warning("Please fill in all fields", {
        style: { background: "#E6C78A", color: "#000" },
      });
      return;
    }

    if (editTrip) {
      setTrips(
        trips.map((trip) =>
          trip._id === editTrip._id ? { ...editTrip, ...newTrip } : trip
        )
      );
      setEditTrip(null);
      toast.success("Trip Updated Successfully");
    } else {
      const newId = trips.length + 1;
      setTrips([
        ...trips,
        {
          ...newTrip,
          _id: `trip${newId}`,
          badge: "New",
          description: newTrip.description,
        },
      ]);
      toast.success("Trip Added Successfully");
    }

    setNewTrip({
      title: "",
      price: "",
      img: "",
      description: "",
      location: "",
      duration: "",
    });
  };

  // Handle edit trip
  const handleEditTrip = (trip) => {
    setNewTrip({
      title: trip.title,
      price: trip.price,
      img: trip.img,
      description: trip.description,
      location: trip.location,
      duration: trip.duration,
    });
    setEditTrip(trip);
  };

  useEffect(() => {
    AOS.init({ duration: 800 });
    gsap.utils.toArray(".trip-item").forEach((element) => {
      gsap.fromTo(
        element,
        { y: 80, scale: 0.95 },
        {
          y: 0,
          scale: 1,
          duration: 1.8,
          ease: "power3.out",
          stagger: 0.25,
          scrollTrigger: {
            trigger: element,
            start: "top 90%",
            end: "top 5%",
            scrub: true,
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, []);

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <Sidebar />
      <main className="p-4 w-full">
        <div className="container mx-auto p-4 max-w-7xl">
          {/* Form inputs */}
          <div className="mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Trip Title"
                className="border p-3 rounded-md text-sm focus:outline-none w-full"
                value={newTrip.title}
                onChange={(e) => setNewTrip({ ...newTrip, title: e.target.value })}
              />
              <input
                type="text"
                placeholder="Price"
                className="border p-3 rounded-md text-sm focus:outline-none w-full"
                value={newTrip.price}
                onChange={(e) => setNewTrip({ ...newTrip, price: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <input
                type="text"
                placeholder="Image URL"
                className="border p-3 rounded-md text-sm focus:outline-none w-full"
                value={newTrip.img}
                onChange={(e) => setNewTrip({ ...newTrip, img: e.target.value })}
              />
              <input
                type="text"
                placeholder="Location"
                className="border p-3 rounded-md text-sm focus:outline-none w-full"
                value={newTrip.location}
                onChange={(e) => setNewTrip({ ...newTrip, location: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <input
                type="text"
                placeholder="Duration"
                className="border p-3 rounded-md text-sm focus:outline-none w-full"
                value={newTrip.duration}
                onChange={(e) => setNewTrip({ ...newTrip, duration: e.target.value })}
              />
              <textarea
                placeholder="Description"
                className="border p-3 rounded-md text-sm focus:outline-none w-full mt-4 sm:mt-0 resize-none"
                value={newTrip.description}
                onChange={(e) => setNewTrip({ ...newTrip, description: e.target.value })}
                rows={4}
              ></textarea>
            </div>
            <button
              onClick={handleAddTrip}
              className="bg-[#E6C78A] text-white px-6 py-2 rounded-md mt-6 text-sm transition-all ease-out transform hover:scale-105 w-full sm:w-auto"
            >
              {editTrip ? "Update Trip" : "Add Trip"}
            </button>
          </div>

          {/* Trips grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
            {trips.map((trip) => (
              <motion.div
  key={trip._id}
  className="trip-item relative group border border-gray-300 rounded-xl shadow-lg bg-white transition-transform duration-300 transform hover:scale-105 hover:shadow-xl flex flex-col"
  data-aos="fade-up"
  layout
>
  <div className="relative w-full h-48 sm:h-56 md:h-48 lg:h-56 overflow-hidden rounded-t-xl">
    <img
      className="w-full h-full object-cover"
      src={trip.img}
      alt={trip.title}
      loading="lazy"
    />
    <div className="absolute top-4 left-4">
      {trip.badge && (
        <span className="bg-gray-800 text-white px-2 py-1 rounded text-xs">
          {trip.badge}
        </span>
      )}
    </div>
    <div className="absolute top-4 right-4 cursor-pointer">
      {wishList.some((item) => item._id === trip._id) ? (
        <BsSuitHeartFill
          size={26}
          className="text-red-500"
          onClick={() => handleWishList(trip)}
        />
      ) : (
        <BsSuitHeart
          size={26}
          className="text-gray-500"
          onClick={() => handleWishList(trip)}
        />
      )}
    </div>

    {/* هنا انتهى div الصورة، لكن الكود نزل تحت داخل نفس motion.div */}
  </div>

  <div className="p-4 flex flex-col flex-grow">
    <h3 className="text-lg font-semibold mb-1 truncate">{trip.title}</h3>
    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
      <MdLocationOn className="text-[#E6C78A]" />
      {trip.location}
    </p>
    <p className="text-sm text-gray-500 flex-grow mb-2 line-clamp-3">
      {trip.description}
    </p>
    <div className="flex justify-between items-center text-sm font-medium text-gray-700 mb-2">
      <span className="text-[#46A29F]">{trip.duration}</span>
      <span className="text-[#E6C78A]">${trip.price}</span>
    </div>
    <div className="flex gap-3 mt-auto">
      <button
        onClick={() => handleEditTrip(trip)}
        className="flex items-center gap-1 bg-[#46A29F] text-white px-3 py-1 rounded-md hover:bg-[#3e7f7f] transition"
      >
        <MdEdit />
        Edit
      </button>
      <button
        onClick={() => handleDeleteTrip(trip._id)}
        className="flex items-center gap-1 bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 transition"
      >
        <MdDelete />
        Delete
      </button>
    </div>
  </div>
</motion.div>
        ))}
      </div>
    </div>
  </main>
</div>
  );
};

export default Trips;
